import moment from "moment";
import { FoaPart, Regulation, type SourcingEventDto } from "../../../autogen/bff-api";
import { isFoaNationalTier } from "../../sourcing-events/buyer/wizard/utils/karens";
import { isDesignContest } from "../../../common/procurement/procedure";
import { citesFoaProvisions } from "../../../common/procurement/publicProcurementOptions";

export type ClarificationRule = "FOA_PART_II" | "FOA_PART_III" | "UNCITED";

// FOA § 9-3 lets a Part II dialogue cover every side of the tenders; § 23-5 limits Part III to clarifying them.
export const clarificationRuleOf = (event: SourcingEventDto | undefined): ClarificationRule => {
  const ctx = event?.eventContext;
  if (ctx?.type !== "Public" || !citesFoaProvisions(ctx.regulation)) return "UNCITED";
  return isFoaNationalTier(ctx.regulation, ctx.foaPart) ? "FOA_PART_II" : "FOA_PART_III";
};

// FOA § 23-5 (1) and forsyningsforskriften § 19-5 (1) tie the request to «en kort tilleggsfrist»; FOA Part II and
// forsyningsforskriften Del I and Del III (§ 5-1, § 26-1 (6)–(7)) do not.
export const clarificationNeedsReplyDeadline = (event: SourcingEventDto | undefined): boolean => {
  const ctx = event?.eventContext;
  if (ctx?.type !== "Public" || isDesignContest(ctx.procedureType)) return false;
  if (ctx.regulation === Regulation.Forsyningsforskriften) return ctx.foaPart === FoaPart.PartIi;
  return (ctx.regulation ?? Regulation.Foa) === Regulation.Foa && ctx.foaPart === FoaPart.PartIii;
};

const SUGGESTED_REPLY_WORKING_DAYS = 3;
const SHORT_ADDITIONAL_DEADLINE_WORKING_DAYS = 10;

export const suggestedReplyDeadline = (from: moment.Moment = moment()): moment.Moment =>
  plusWorkingDays(from, SUGGESTED_REPLY_WORKING_DAYS).set({ hour: 12, minute: 0, second: 0, millisecond: 0 });

export const runningTenderValidityEnd = (
  event: SourcingEventDto | undefined,
  now: moment.Moment = moment(),
): string | null => {
  const tenderValidityEnd = event?.milestoneDates?.tenderValidityEnd;
  return tenderValidityEnd != null && moment(tenderValidityEnd).isAfter(now) ? tenderValidityEnd : null;
};

export const tenderValidityHasPassed = (
  event: SourcingEventDto | undefined,
  now: moment.Moment = moment(),
): boolean => {
  const tenderValidityEnd = event?.milestoneDates?.tenderValidityEnd;
  return tenderValidityEnd != null && !moment(tenderValidityEnd).isAfter(now);
};

export const replyDeadlineAfterTenderValidity = (event: SourcingEventDto | undefined, replyDeadline: string): boolean => {
  const tenderValidityEnd = runningTenderValidityEnd(event);
  return tenderValidityEnd != null && moment(replyDeadline).isAfter(moment(tenderValidityEnd));
};

export const replyDeadlineLongerThanShort = (replyDeadline: string, from: moment.Moment = moment()): boolean =>
  moment(replyDeadline).isAfter(plusWorkingDays(from, SHORT_ADDITIONAL_DEADLINE_WORKING_DAYS).endOf("day"));

const plusWorkingDays = (from: moment.Moment, workingDays: number): moment.Moment => {
  const day = from.clone();
  let added = 0;
  while (added < workingDays) {
    day.add(1, "day");
    if (day.isoWeekday() <= 5) added += 1;
  }
  return day;
};
